import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup
} from '@atlaskit/dropdown-menu';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import Textfield from '@atlaskit/textfield';
import { G300 } from '@atlaskit/theme/colors';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { messages } from '../../../../../messages/sign-up-form';
import {
  AnalyticsTrackType,
  Product,
  ProductKeys,
  Sites
} from '../../../../../model/wac-signup-form';
import { InfoBox } from './';

import {
  isProductIncluded,
  useSignUpFormUIState,
  useSitePermission
} from '../../../../../hook';
import {
  DomainToolTip,
  ToolTipText,
  ToolTipContainer,
  useDocumentClick
} from './site-name-field';
import { B400, N50 } from '@atlaskit/theme/colors';
import { TooltipIcon } from '../Icons/tooltip-icon';

import {
  CompoundField,
  DropdownButtonStyle,
  DropdownButtonStyleOpen,
  DropdownTrigger,
  ErrorMessageWithDefaultClassName,
  FieldContainer,
  FieldErrorState,
  JswTrialNotice,
  Label,
  SiteNameAfter,
  SiteNameAfterLabel,
  SitePickerTextfieldWrapper,
  SitePickerToggle,
  StyledInfoBoxWrapper,
  UnderLabelMessage
} from './styled';

export type SitePickerProps = {
  onSiteNameChange: (selectedSite: Sites, cloudName: string) => void;
  onSiteNameStartNew: () => void;
  sites: Sites[];
  isBundleActive: boolean;
  siteNameAfter: string;
  analyticsTrack: AnalyticsTrackType;
  isFree: boolean;
  setIsValidSiteName: (val: boolean) => void;
  isDisabled: boolean;
  showFreeLimitExceededError: boolean;
  selectedSiteId: string;
  productKeys: Product[];
  isDeferredSiteNameEnabled?: boolean;
  fadeOnDisable?: boolean;
};

export type FormattedSite = {
  readonly name: string;
  readonly value: string;
  readonly products?: string[];
};

export type FormattedProduct = {
  readonly title: FormattedMessage.MessageDescriptor;
  readonly sites: FormattedSite[];
};

type OnOpenChangeProps = {
  readonly isOpen: boolean;
  readonly event?: { [key: string]: any };
};

const getSiteById = (cloudId: string, sites: Sites[]) => {
  const matchedSites = sites.filter((site) => site.cloudId === cloudId);
  return matchedSites.length > 0 ? matchedSites[0] : sites[0];
};

export const isSelectedSiteValid = (
  sitesByCategory: {
    [key: string]: FormattedProduct;
  },
  siteId: string
) => {
  return (
    sitesByCategory?.withJiraSoftware?.sites?.find(
      (site) => site.value === siteId
    ) ||
    sitesByCategory?.withoutJiraSoftware?.sites?.find(
      (site) => site.value === siteId
    )
  );
};

function productKeyToSplitSitesBy(productKeys: Product[]) {
  if (
    isProductIncluded(productKeys, ProductKeys.JIRA_SERVICE_DESK) ||
    isProductIncluded(productKeys, ProductKeys.JIRA_SERVICE_MANAGEMENT)
  ) {
    return {
      siteKey: ProductKeys.CONFLUENCE,
      recommendedSitesTitle: messages.dropdownWithRecommended,
      otherSitesTitle: messages.dropdownWithoutRecommended
    };
  } else {
    return {
      siteKey: ProductKeys.JIRA_SOFTWARE,
      recommendedSitesTitle: messages.dropdownWithJiraSoftware,
      otherSitesTitle: messages.dropdownWithoutJiraSoftware
    };
  }
}
export function orderSpecificProductFirstSort(
  a: FormattedSite,
  b: FormattedSite
) {
  if (
    a?.products?.includes(ProductKeys.CONFLUENCE) &&
    !b?.products?.includes(ProductKeys.CONFLUENCE)
  ) {
    return -1;
  }
  if (
    !a?.products?.includes(ProductKeys.CONFLUENCE) &&
    b?.products?.includes(ProductKeys.CONFLUENCE)
  ) {
    return 1;
  }
  if (
    a?.products?.includes(ProductKeys.CONFLUENCE) &&
    b?.products?.includes(ProductKeys.CONFLUENCE)
  ) {
    return 0;
  }
  return 0;
}

export function SitePickerField({
  isBundleActive,
  sites,
  siteNameAfter,
  onSiteNameStartNew,
  onSiteNameChange,
  setIsValidSiteName,
  isFree,
  isDisabled,
  showFreeLimitExceededError = false,
  selectedSiteId,
  productKeys,
  isDeferredSiteNameEnabled,
  fadeOnDisable,
  analyticsTrack
}: SitePickerProps) {
  // const isSiteAdmin = false; // TODO change to use hook
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeSite, setActiveSite] = useState(sites[0]);
  const [triggerText, setTriggerText] = useState({ ...messages.selectSite });
  const [hideJswTrialNotice, setHideJswTrialNotice] = useState(true);
  const [showConfluenceNotice, setShowConfluenceNotice] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [toolTipOpen, setToolTipOpen] = useState(false);
  const { isBeacon } = useSignUpFormUIState();
  const { isPermitted, isValidating } = useSitePermission(
    activeSite.cloudId,
    isBundleActive
  );

  const showSiteSwitcher = sites.length > 1;
  const isJsm =
    isProductIncluded(productKeys, ProductKeys.JIRA_SERVICE_DESK) ||
    isProductIncluded(productKeys, ProductKeys.JIRA_SERVICE_MANAGEMENT);
  const hasSitesWithConfluence = sites.some((site) => {
    return site?.products.some((p) => p === ProductKeys.CONFLUENCE);
  });

  const toolTipTrigger = 'domain-name-tooltip-trigger';
  useDocumentClick(toolTipOpen, setToolTipOpen, toolTipTrigger);

  const { segment } = analyticsTrack;

  const useRecomendedSitesInDropdown = isBundleActive || isJsm;

  const categorizedSitesData = useMemo(() => {
    const siteKeyDataToSplitBy = productKeyToSplitSitesBy(productKeys);
    const sitesByCategory: { [key: string]: FormattedProduct } = {
      withRecommendedSite: {
        title: siteKeyDataToSplitBy.recommendedSitesTitle,
        sites: []
      },
      withoutRecommendedSite: {
        title: siteKeyDataToSplitBy.otherSitesTitle,
        sites: []
      }
    };

    sites = _.sortBy(sites, ['displayName']);

    const addSiteToResult = (hasJsw: boolean, site: FormattedSite) => {
      if (hasJsw) {
        sitesByCategory.withRecommendedSite.sites.push(site);
      } else {
        sitesByCategory.withoutRecommendedSite.sites.push(site);
      }
    };

    sites.map((site) => {
      addSiteToResult(site.products.includes(siteKeyDataToSplitBy.siteKey), {
        value: site.cloudId,
        name: site.displayName,
        products: site.products
      });
    });

    const displaySitesFromAllCategories =
      sitesByCategory.withRecommendedSite.sites.length > 0 &&
      sitesByCategory.withoutRecommendedSite.sites.length > 0;

    const displayOnlySitesContainingJiraSoftware =
      sitesByCategory.withRecommendedSite.sites.length > 0 &&
      sitesByCategory.withoutRecommendedSite.sites.length === 0;

    const displayOnlySitesMissingJiraSoftware =
      sitesByCategory.withRecommendedSite.sites.length === 0 &&
      sitesByCategory.withoutRecommendedSite.sites.length > 0;

    return {
      sitesByCategory,
      displaySitesFromAllCategories,
      displayOnlySitesContainingJiraSoftware,
      displayOnlySitesMissingJiraSoftware
    };
  }, [sites]);

  useEffect(() => {
    if (isLoaded) {
      if (selectedSiteId) {
        const found = isSelectedSiteValid(
          categorizedSitesData.sitesByCategory,
          selectedSiteId
        );
        if (found) {
          handleDropdownItemClick(found);
          return;
        }
      }
      if (categorizedSitesData.sitesByCategory?.withRecommendedSite?.sites[0]) {
        handleDropdownItemClick(
          categorizedSitesData.sitesByCategory.withRecommendedSite.sites[0]
        );
      } else if (
        categorizedSitesData.sitesByCategory?.withoutRecommendedSite?.sites[0]
      ) {
        handleDropdownItemClick(
          categorizedSitesData.sitesByCategory.withoutRecommendedSite.sites[0]
        );
      }
      if (isJsm) {
        const confluenceOrderedFirst = [
          ...categorizedSitesData.sitesByCategory.withRecommendedSite.sites,
          ...categorizedSitesData.sitesByCategory.withoutRecommendedSite.sites
        ].sort(orderSpecificProductFirstSort);
        handleDropdownItemClick(confluenceOrderedFirst[0]);
      }
    } else {
      setIsLoaded(true);
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isJsm) {
      const siteHasConf = activeSite?.products.some(
        (p) => p === ProductKeys.CONFLUENCE
      );
      setShowConfluenceNotice(
        !siteHasConf && hasSitesWithConfluence && !showFreeLimitExceededError
      );
    }
  }, [activeSite]);

  useEffect(() => {
    setIsValidSiteName(isPermitted);
  }, [isPermitted]);

  const trigger = (
    <DropdownTrigger>
      <FormattedMessage {...triggerText} />
    </DropdownTrigger>
  );

  const handleDropdownItemClick = ({ name, value }: FormattedSite) => {
    const selectedSite = getSiteById(value, sites);
    onSiteNameChange(selectedSite, selectedSite.displayName);
    setActiveSite(selectedSite);
    setTriggerText(
      Object.assign({}, messages.selectSite, {
        defaultMessage: name + siteNameAfter
      })
    );
    setHideJswTrialNotice(
      isFree || selectedSite.products.includes(ProductKeys.JIRA_SOFTWARE)
    );
  };

  const triggerButtonTheme = () => {
    const buttonStyles = !isDropdownOpen
      ? DropdownButtonStyle
      : Object.assign({}, DropdownButtonStyle, DropdownButtonStyleOpen);
    return {
      buttonStyles,
      spinnerStyles: {}
    };
  };

  const dropDownItems = (sitesData: FormattedSite[]) => {
    return sitesData.map((site: FormattedSite) => {
      return (
        <DropdownItem
          key={site.name}
          onClick={() => handleDropdownItemClick(site)}
        >
          {site.name + siteNameAfter}
        </DropdownItem>
      );
    });
  };

  return (
    <FieldContainer className={'site-picker-field sign-up-field-container'}>
      <CompoundField>
        <Label>
          {isDeferredSiteNameEnabled ? (
            <FormattedMessage {...messages.siteNameLabel} />
          ) : (
            <FormattedMessage {...messages.yourSite} />
          )}
        </Label>
        {isDeferredSiteNameEnabled && (
          <ToolTipContainer>
            <DomainToolTip
              isDisabled={isDisabled}
              fadeOnDisable={fadeOnDisable}
              data-testid="domain-tool-tip"
              className={toolTipOpen ? 'selected' : ''}
              onClick={() => {
                if (isDisabled) {
                  return;
                }
                if (!toolTipOpen) {
                  segment('bxp.formCloudSignup.toolTipDisplayed', {});
                }
                setToolTipOpen(!toolTipOpen);
              }}
            >
              <TooltipIcon
                fillColor={toolTipOpen && !isDisabled ? B400 : N50}
              />
            </DomainToolTip>
            {toolTipOpen && (
              <ToolTipText
                className={toolTipTrigger}
                isDisabled={isDisabled}
                fadeOnDisable={fadeOnDisable}
              >
                <FormattedMessage {...messages.deferredSiteNameTooltipText} />
              </ToolTipText>
            )}
          </ToolTipContainer>
        )}
      </CompoundField>
      {isDeferredSiteNameEnabled && (
        <UnderLabelMessage>
          <FormattedMessage {...messages.siteNameOptionalMessageN2E} />
        </UnderLabelMessage>
      )}
      <FieldErrorState showError={showFreeLimitExceededError}>
        {showSiteSwitcher && (
          <DropdownMenu
            trigger={trigger}
            triggerType="button"
            shouldFitContainer={true}
            isLoading={isValidating}
            triggerButtonProps={{
              shouldFitContainer: true,
              theme: triggerButtonTheme,
              isDisabled
            }}
            onOpenChange={({ isOpen }: OnOpenChangeProps) =>
              setIsDropdownOpen(isOpen)
            }
          >
            {useRecomendedSitesInDropdown && (
              <>
                {categorizedSitesData.displaySitesFromAllCategories && (
                  <>
                    <DropdownItemGroup
                      key="withJiraSoftware"
                      title={
                        <FormattedMessage
                          {...categorizedSitesData.sitesByCategory
                            .withRecommendedSite.title}
                        />
                      }
                    >
                      {dropDownItems(
                        categorizedSitesData.sitesByCategory.withRecommendedSite
                          .sites
                      )}
                    </DropdownItemGroup>
                    <DropdownItemGroup
                      key="withoutJiraSoftware"
                      title={
                        <FormattedMessage
                          {...categorizedSitesData.sitesByCategory
                            .withoutRecommendedSite.title}
                        />
                      }
                    >
                      {dropDownItems(
                        categorizedSitesData.sitesByCategory
                          .withoutRecommendedSite.sites
                      )}
                    </DropdownItemGroup>
                  </>
                )}
                {categorizedSitesData.displayOnlySitesContainingJiraSoftware &&
                  dropDownItems(
                    categorizedSitesData.sitesByCategory.withRecommendedSite
                      .sites
                  )}
                {categorizedSitesData.displayOnlySitesMissingJiraSoftware &&
                  dropDownItems(
                    categorizedSitesData.sitesByCategory.withoutRecommendedSite
                      .sites
                  )}
              </>
            )}
            {!useRecomendedSitesInDropdown && [
              ...dropDownItems(
                categorizedSitesData.sitesByCategory.withRecommendedSite.sites
              ),
              ...dropDownItems(
                categorizedSitesData.sitesByCategory.withoutRecommendedSite
                  .sites
              )
            ]}
          </DropdownMenu>
        )}

        {!showSiteSwitcher && (
          <SitePickerTextfieldWrapper>
            <Textfield
              isRequired
              className={'site-picker-field input'}
              value={sites[0].displayName}
              style={{ height: 'inherit', width: '60%', color: '#091E44' }}
              isDisabled={true}
              elemAfterInput={
                <SiteNameAfter>
                  <SiteNameAfterLabel>{siteNameAfter}</SiteNameAfterLabel>
                </SiteNameAfter>
              }
            />
          </SitePickerTextfieldWrapper>
        )}
      </FieldErrorState>

      {isPermitted && !hideJswTrialNotice && isBundleActive && (
        <JswTrialNotice>
          <CheckCircleIcon label="" primaryColor={G300} size="medium" />
          <FormattedMessage {...messages.jswTrialNotice} />
        </JswTrialNotice>
      )}

      {!isPermitted && (
        <ErrorMessageWithDefaultClassName>
          <FormattedMessage {...messages.siteAdminManageError} />
        </ErrorMessageWithDefaultClassName>
      )}

      {showFreeLimitExceededError && (
        <ErrorMessageWithDefaultClassName>
          <FormattedMessage {...messages.freeBreachLimitExceededError} />
        </ErrorMessageWithDefaultClassName>
      )}

      {!isBeacon && ( // Beacon cannot be used to create new site, so disable the toggle
        <SitePickerToggle
          onClick={() => {
            if (!isDisabled) {
              onSiteNameStartNew();
            }
          }}
        >
          {isDeferredSiteNameEnabled ? (
            <FormattedMessage {...messages.createNewSite} />
          ) : (
            <FormattedMessage {...messages.newSite} />
          )}
        </SitePickerToggle>
      )}

      {showConfluenceNotice && (
        <StyledInfoBoxWrapper className="jsm-confluence-notice">
          <InfoBox>
            <p>
              <FormattedMessage {...messages.jsmConfluenceNotice} />
            </p>
          </InfoBox>
        </StyledInfoBoxWrapper>
      )}
    </FieldContainer>
  );
}
